import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import { getStationName } from './utils/stationMapping';

function FlexibleShiftPlanning({ user }) {
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [userStatuses, setUserStatuses] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSourceDate, setCloneSourceDate] = useState('');
  const [cloneTargetDate, setCloneTargetDate] = useState('');
  const [cashierFilter, setCashierFilter] = useState(false); // Kassenkraft-Filter
  const [editingShift, setEditingShift] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Toast-Funktion
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Stationen (inkl. Wiesn-Garten/Bierausschank). Stations-Planer (role
  // 'planner', z.B. Jennifer) sehen und bearbeiten NUR ihre eigene Station –
  // das Backend erzwingt das zusätzlich serverseitig.
  const ALL_STATIONS = [
    { id: 'G1', name: 'Station G1', color: '#FF6B6B' },
    { id: 'G5', name: 'Station G5', color: '#4ECDC4' },
    { id: 'G8', name: 'Station G8', color: '#45B7D1' },
    { id: 'G10', name: 'Station G10', color: '#96CEB4' },
    { id: 'WG', name: 'Wiesn-Garten', color: '#8BC34A' }
  ];
  const stations = user?.role === 'planner'
    ? ALL_STATIONS.filter(s => s.id === ((user.planner_station || 'WG').toUpperCase()))
    : ALL_STATIONS;

  // Frühöffnung: Der Wiesn-Garten darf an SAMSTAGEN und FEIERTAGEN schon ab
  // 05:00 öffnen – nur dann (und nur für WG) sind die 5/6-Uhr-Spalten bearbeitbar.
  const EARLY_STATIONS = ['WG'];
  const HOLIDAYS = ['2026-10-03']; // Feiertage im Wiesn-Zeitraum
  const isEarlyOpeningDay = (dateStr) => !!dateStr
    && (new Date(dateStr + 'T12:00:00').getDay() === 6 || HOLIDAYS.includes(dateStr));
  const isHourEditable = (stationId, hour) =>
    hour >= 7 || (EARLY_STATIONS.includes(String(stationId || '').toUpperCase()) && isEarlyOpeningDay(selectedDate));

  // Stunden von 5:00 bis 01:00 (nächster Tag) = 20 Stunden. Die 5/6-Uhr-Spalten
  // erscheinen nur an Frühöffnungs-Tagen (siehe getActiveHours/isHourEditable).
  const hours = Array.from({ length: 20 }, (_, i) => i + 5);

  // Kompatibilitätsfunktion: Konvertiert neues Objekt-Format zurück zu Array-Format
  const normalizeStations = (dayPlan) => {
    if (!dayPlan || !dayPlan.stations) return dayPlan;
    
    // Wenn stations bereits ein Array ist, nichts ändern
    if (Array.isArray(dayPlan.stations)) {
      return dayPlan;
    }
    
    // Wenn stations ein Objekt ist (neues Format), konvertiere zu Array
    if (typeof dayPlan.stations === 'object') {
      console.log('Konvertiere stations Objekt zu Array für Kompatibilität');
      const stationsArray = Object.entries(dayPlan.stations).map(([stationName, stationData]) => ({
        station_id: stationName.replace('Station ', ''),
        hourlyStaffing: {},
        assignments: [],
        hours: stationData.hours || []
      }));
      
      return {
        ...dayPlan,
        stations: stationsArray
      };
    }
    
    return dayPlan;
  };

  // Funktion zur korrekten Stundenanzeige (24:00, 25:00 statt 00:00, 01:00)
  const formatHour = (hour) => {
    if (hour === 24) return '00:00';
    if (hour === 25) return '01:00';
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const formatHourRange = (hour) => {
    const startHour = formatHour(hour);
    const endHour = formatHour(hour + 1);
    return `${startHour} - ${endHour}`;
  };

  // Funktion zur Berechnung verfügbarer Personen pro Stunde basierend auf tatsächlicher Verfügbarkeit
  const getAvailableEmployeesCount = (hour) => {
    // Berechne bereits eingeteilte Personen zu dieser Stunde
    const assignedUserIds = new Set();
    normalizeStations(dayPlan).stations.forEach(station => {
      const assigned = station.hourlyStaffing[hour]?.assigned || [];
      assigned.forEach(userId => assignedUserIds.add(userId));
    });
    
    // Debug: Log der Eingabeparameter
    if (hour === 7) { // Log nur für 7 Uhr um Spam zu vermeiden
      console.log('getAvailableEmployeesCount Debug für Stunde 7:', {
        hour,
        selectedDate,
        userAvailabilitiesLength: userAvailabilities.length,
        userAvailabilities: userAvailabilities,
        usersLength: users.length,
        assignedUserIds: Array.from(assignedUserIds)
      });
    }
    
    // Zähle verfügbare Personen für diese Stunde am gewählten Datum
    let availableCount = 0;
    let debugInfo = [];
    
    users.forEach(user => {
      // Überspringe bereits eingeteilte Personen
      if (assignedUserIds.has(user.id)) {
        return;
      }
      
      // Prüfe ob Person zu dieser Stunde verfügbar ist
      const userAvailability = userAvailabilities.find(av => 
        av.user_id === user.id && av.date === selectedDate && av.is_available === 1
      );
      
      if (userAvailability) {
        // Konvertiere Zeiten zu Minuten für einfacheren Vergleich
        const startMinutes = timeToMinutes(userAvailability.start_time);
        const endMinutes = timeToMinutes(userAvailability.end_time);
        const currentMinutes = hour * 60; // hour in Minuten
        
        // Prüfe ob aktuelle Stunde im Verfügbarkeitszeitraum liegt
        let isAvailable = false;
        if (userAvailability.end_time === '01:00') {
          // Spezialfall: Verfügbarkeit bis 01:00 nächster Tag (25:00 in unserem System)
          isAvailable = currentMinutes >= startMinutes || currentMinutes <= 60; // bis 01:00
        } else {
          isAvailable = currentMinutes >= startMinutes && currentMinutes < endMinutes;
        }
        
        if (isAvailable) {
          availableCount++;
          debugInfo.push(user.name);
          if (hour === 7) { // Debug-Log für Verfügbarkeit um 7 Uhr
            console.log(`User ${user.name} ist verfügbar um ${hour}:00`, {
              userAvailability,
              startMinutes,
              endMinutes,
              currentMinutes,
              isAvailable
            });
          }
        }
      } else if (hour === 7) {
        console.log(`Keine Verfügbarkeit gefunden für User ${user.name} (ID: ${user.id})`);
      }
    });
    
    if (hour === 7) {
      console.log(`Gesamtergebnis für Stunde ${hour}: ${availableCount} verfügbare Mitarbeiter`, debugInfo);
    }
    
    return availableCount;
  };

  // Hilfsfunktion: Zeit-String zu Minuten konvertieren
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const [dayPlan, setDayPlan] = useState({
    date: selectedDate,
    startTime: '07:00',
    endTime: '01:00', // Bis 1 Uhr nachts (25:00)
    stations: stations.map(station => ({
      station_id: station.id,
      hourlyStaffing: hours.reduce((acc, hour) => {
        acc[hour] = { 
          needed: 0, 
          assigned: [],
          needsCashier: hour < 22 // Standard: Kassenkraft bis 22 Uhr benötigt
        };
        return acc;
      }, {})
    }))
  });

  const [currentPlanId, setCurrentPlanId] = useState(null);

  const [plannedDays, setPlannedDays] = useState([]);
  const [userAvailabilities, setUserAvailabilities] = useState([]);
  
  // Lade gespeicherte Datumsauswahl aus localStorage oder verwende Standardwerte
  const loadSavedDateRange = () => {
    try {
      const savedRange = localStorage.getItem('planningOverview_dateRange');
      if (savedRange) {
        const parsed = JSON.parse(savedRange);
        // Prüfe ob die gespeicherten Daten gültig sind
        if (parsed.start && parsed.end) {
          return parsed;
        }
      }
    } catch (error) {
      console.log('Fehler beim Laden der gespeicherten Datumsauswahl:', error);
    }
    
    // Standardwerte: Heute bis +14 Tage
    return {
      start: new Date().toISOString().split('T')[0],
      end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    };
  };
  
  const [dateRange, setDateRange] = useState(loadSavedDateRange());

  const fetchPlannedDays = async () => {
    try {
      const response = await apiCall(`/flexible-shifts/planned-days?start=${dateRange.start}&end=${dateRange.end}`);
      if (response.ok) {
        const data = response;
        setPlannedDays(data.map(item => item.date));
      }
    } catch (err) {
      console.error('Fehler beim Laden der geplanten Tage:', err);
    }
  };

  useEffect(() => {
    console.log('useEffect triggered with selectedDate:', selectedDate);
    fetchUsers();
    fetchShiftsForDate(selectedDate);
    fetchUserAvailabilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, cashierFilter]); // Lade Daten neu wenn sich der cashierFilter ändert

  // Lade auch Verfügbarkeiten neu wenn sich das Datum ändert
  useEffect(() => {
    if (users.length > 0) {
      fetchUserAvailabilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, users]);

  // Lade geplante Tage beim ersten Laden und wenn sich der Zeitraum ändert
  useEffect(() => {
    fetchPlannedDays();
  }, [dateRange]);

  // Aktualisiere das dayPlan.date wenn selectedDate sich ändert
  useEffect(() => {
    setDayPlan(prevPlan => ({
      ...prevPlan,
      date: selectedDate
    }));
  }, [selectedDate]);

  const fetchUsers = async () => {
    try {
      const data = await apiCall('/users');
      console.log('Alle Benutzer geladen:', data.length);
      console.log('Erste 3 Benutzer mit vollständigen Daten:', data.slice(0, 3).map(u => ({ 
        id: u.id, 
        name: u.name, 
        firstname: u.firstname, 
        lastname: u.lastname, 
        role: u.role 
      })));
      
      // Filtere nur Mitarbeiter und dann zusätzlich Kassenkräfte wenn benötigt
      const employees = Array.isArray(data) ? data.filter(u => ['employee', 'mitarbeiter', 'lead', 'planner'].includes(u.role)) : [];
      
      // Wenn Kassenkraft gefordert ist, nur Mitarbeiter mit can_work_cash_register=1 anzeigen
      const filteredForCashier = cashierFilter 
        ? employees.filter(e => e.can_work_cash_register === 1) 
        : employees;
      
      console.log('Gefilterte Mitarbeiter:', filteredForCashier.length, 
                  cashierFilter ? '(nur Kassenkräfte)' : '(alle Mitarbeiter)');
      console.log('Erste 3 Mitarbeiter Namen:', filteredForCashier.slice(0, 3).map(e => e.name));
      setUsers(filteredForCashier);
      
      // Status für alle Mitarbeiter laden
      const statuses = {};
      for (const emp of employees) {
        try {
          const statusData = await apiCall(`/users/${emp.id}/document-status`);
          statuses[emp.id] = statusData;
        } catch (err) {
          console.error(`Fehler beim Laden des Status für ${emp.name}:`, err);
        }
      }
      setUserStatuses(statuses);
    } catch (err) {
      console.error('Fehler beim Laden der Mitarbeiter:', err);
    }
  };

  const fetchUserAvailabilities = async () => {
    try {
      const data = await apiCall(`/user-availability?startDate=${selectedDate}&endDate=${selectedDate}`);
      console.log('Verfügbarkeitsdaten geladen für', selectedDate, ':', data);
      console.log('Anzahl der Verfügbarkeitsdaten:', data.length);
      console.log('Beispiel Verfügbarkeitsdaten:', data.slice(0, 3));
      setUserAvailabilities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fehler beim Laden der Verfügbarkeiten:', err);
      setUserAvailabilities([]);
    }
  };

  const fetchShiftsForDate = async (date) => {
    console.log('fetchShiftsForDate called with date:', date);
    try {
      const url = `/flexible-shifts?date=${date}`;
      console.log('Fetching from URL:', url);
      
      const data = await apiCall(url);
      console.log('Received data:', data);
      
      if (Array.isArray(data) && data.length > 0) {
        // Bestehende Schichten in dayPlan laden
        const planData = JSON.parse(data[0].plan_data);
        console.log('Loading existing plan data:', planData);
        
        // Sicherstellen, dass startTime und endTime gesetzt sind
        const validatedPlanData = {
          ...planData,
          startTime: planData.startTime || '07:00',
          endTime: planData.endTime || '01:00'
        };

        // Neu hinzugekommene Stationen (z.B. Wiesn-Garten) in ÄLTERE Tagespläne
        // ergänzen – sonst wären sie für bereits geplante Tage nie bearbeitbar.
        const loadedStations = Array.isArray(validatedPlanData.stations)
          ? validatedPlanData.stations
          : Object.values(validatedPlanData.stations || {});
        const missingStations = stations
          .filter(s => !loadedStations.some(ls =>
            String(ls.station_id || ls.id || '').toUpperCase() === s.id.toUpperCase()))
          .map(station => ({
            station_id: station.id,
            hourlyStaffing: hours.reduce((acc, hour) => {
              acc[hour] = { needed: 0, assigned: [], needsCashier: false };
              return acc;
            }, {})
          }));
        validatedPlanData.stations = [...loadedStations, ...missingStations];

        setDayPlan(validatedPlanData);
        setCurrentPlanId(data[0].id); // Plan-ID speichern für Updates
      } else {
        console.log('No existing data found, creating new plan for date:', date);
        // Neuen Plan für das Datum erstellen
        const defaultStartTime = '07:00';
        const defaultEndTime = '01:00';
        
        // Berechne aktive Stunden basierend auf Start- und Endzeit
        const startHour = parseInt(defaultStartTime.split(':')[0]);
        let endHour = parseInt(defaultEndTime.split(':')[0]);
        if (endHour < startHour) {
          endHour = endHour + 24; // Nächster Tag
        }
        
        const newPlan = {
          date: date,
          startTime: defaultStartTime,
          endTime: defaultEndTime,
          stations: stations.map(station => ({
            station_id: station.id,
            hourlyStaffing: hours.reduce((acc, hour) => {
              // Nur Stunden innerhalb der aktiven Zeit bekommen needed: 1, alle anderen needed: 0
              const isActiveHour = hour >= startHour && hour <= endHour;
              acc[hour] = { 
                needed: isActiveHour ? 1 : 0,
                assigned: [],
                needsCashier: hour < 22 // Standard: Kassenkraft bis 22 Uhr benötigt
              };
              return acc;
            }, {})
          }))
        };
        setDayPlan(newPlan);
        setCurrentPlanId(null); // Kein bestehender Plan
      }
    } catch (err) {
      console.error('Fehler beim Laden der Schichten:', err);
    }
  };

  const updateStaffingNeed = (stationIndex, hour, count) => {
    console.log('updateStaffingNeed called:', stationIndex, hour, count);
    const newDayPlan = JSON.parse(JSON.stringify(dayPlan)); // Deep clone
    if (!newDayPlan.stations[stationIndex]) {
      console.error('Station not found:', stationIndex);
      return;
    }
    if (!newDayPlan.stations[stationIndex].hourlyStaffing[hour]) {
      // Ältere Pläne kennen die 5/6-Uhr-Slots noch nicht → bei Bedarf anlegen
      newDayPlan.stations[stationIndex].hourlyStaffing[hour] = { needed: 0, assigned: [], needsCashier: false };
    }
    newDayPlan.stations[stationIndex].hourlyStaffing[hour].needed = parseInt(count) || 0;
    console.log('Updated dayPlan:', newDayPlan);
    setDayPlan(newDayPlan);
    // Automatisches Speichern nach kurzer Verzögerung (frischen Stand mitgeben!)
    setTimeout(() => saveDayPlan(newDayPlan), 1000);
  };

  const updateCashierNeed = (stationIndex, hour, needsCashier) => {
    const newDayPlan = { ...dayPlan };
    if (!newDayPlan.stations[stationIndex].hourlyStaffing[hour]) {
      newDayPlan.stations[stationIndex].hourlyStaffing[hour] = { needed: 0, assigned: [], needsCashier: false };
    }
    newDayPlan.stations[stationIndex].hourlyStaffing[hour].needsCashier = needsCashier;
    setDayPlan(newDayPlan);
    // Automatisches Speichern nach kurzer Verzögerung (frischen Stand mitgeben!)
    setTimeout(() => saveDayPlan(newDayPlan), 1000);
  };

  const assignEmployee = (stationIndex, hour, userId) => {
    const newDayPlan = { ...dayPlan };
    const slot = newDayPlan.stations[stationIndex].hourlyStaffing[hour];
    
    if (!slot.assigned.includes(userId)) {
      slot.assigned.push(userId);
    }
    
    setDayPlan(newDayPlan);
    // Automatisches Speichern nach kurzer Verzögerung (frischen Stand mitgeben!)
    setTimeout(() => saveDayPlan(newDayPlan), 1000);
  };

  const removeEmployee = (stationIndex, hour, userId) => {
    const newDayPlan = { ...dayPlan };
    const slot = newDayPlan.stations[stationIndex].hourlyStaffing[hour];
    
    slot.assigned = slot.assigned.filter(id => id !== userId);
    
    setDayPlan(newDayPlan);
    // Automatisches Speichern nach kurzer Verzögerung (frischen Stand mitgeben!)
    setTimeout(() => saveDayPlan(newDayPlan), 1000);
  };

  // WICHTIG: planToSave explizit übergeben! Der verzögerte Auto-Save aus einem
  // setTimeout hält sonst den ALTEN State fest (stale closure) und speichert
  // den Stand von VOR der letzten Eingabe – dadurch "verschwanden" z.B. frisch
  // auf 0 gesetzte Bedarfe nach dem Neuladen.
  const saveDayPlan = async (planToSave) => {
    const plan = planToSave || dayPlan;
    try {
      const isUpdate = currentPlanId !== null;
      console.log('Saving plan:', { isUpdate, currentPlanId, date: plan.date });

      if (isUpdate) {
        // Update existing plan
        const result = await apiCall(`/flexible-shifts/${currentPlanId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: plan.date,
            plan_data: JSON.stringify(plan)
          })
        });
        console.log('Update result:', result);
      } else {
        // Create new plan
        const result = await apiCall('/flexible-shifts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date: plan.date,
            plan_data: JSON.stringify(plan)
          })
        });
        console.log('Create result:', result);
        
        // Wenn es ein neuer Plan war, speichere die neue ID
        if (result.id) {
          setCurrentPlanId(result.id);
          console.log('Set new plan ID:', result.id);
        }
      }
      
      showToast('Schichtplan automatisch gespeichert!', 'success');
      // Geplante Tage neu laden, damit der Kalender aktualisiert wird
      fetchPlannedDays();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      showToast('Fehler beim Speichern!', 'danger');
    }
  };

  const cloneDayPlan = async (sourceDate, targetDate) => {
    try {
      // Lade den Quell-Plan
      const data = await apiCall(`/flexible-shifts?date=${sourceDate}`);
      
      if (Array.isArray(data) && data.length > 0) {
        const sourcePlan = JSON.parse(data[0].plan_data);
        
        // Erstelle neuen Plan mit dem Zieldatum
        const clonedPlan = {
          ...sourcePlan,
          date: targetDate,
          stations: sourcePlan.stations.map(station => ({
            ...station,
            hourlyStaffing: Object.keys(station.hourlyStaffing).reduce((acc, hour) => {
              acc[hour] = {
                needed: station.hourlyStaffing[hour].needed,
                needsCashier: station.hourlyStaffing[hour].needsCashier,
                assigned: [] // Mitarbeiter werden nicht geklont, nur der Bedarf
              };
              return acc;
            }, {})
          }))
        };
        
        // Speichere den geklonten Plan
        const result = await apiCall('/flexible-shifts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: targetDate, plan_data: JSON.stringify(clonedPlan) })
        });
        
        console.log('Clone result:', result);
        showToast(`Plan von ${sourceDate} erfolgreich nach ${targetDate} geklont!`, 'success');
        // Wenn das Zieldatum das aktuell gewählte ist, lade es neu
        if (targetDate === selectedDate) {
          fetchShiftsForDate(targetDate);
        }
      } else {
        showToast('Kein Plan für das Quelldatum gefunden!', 'warning');
      }
    } catch (err) {
      console.error('Fehler beim Klonen:', err);
      showToast('Fehler beim Klonen des Plans!', 'danger');
    }
  };

  const generateIndividualShifts = async () => {
    if (!hasAnyAssignments()) {
      showToast('Bitte zuerst Mitarbeiter zu den Stationen zuweisen!', 'warning');
      return;
    }

    try {
      const response = await apiCall('/flexible-shifts/generate-individual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDate, dayPlan })
      });
      
      if (response.ok) {
        const result = response;
        showToast(`${result.count} individuelle Schichten erstellt!`, 'success');
        fetchShiftsForDate(selectedDate);
      } else {
        showToast('Fehler beim Erstellen der Schichten!', 'danger');
      }
    } catch (err) {
      console.error('Fehler beim Erstellen der Schichten:', err);
      showToast('Fehler beim Erstellen der Schichten!', 'danger');
    }
  };

  const getUserStatusColor = (userId) => {
    const status = userStatuses[userId];
    if (!status) return '#6c757d';
    
    if (status.allComplete) return '#28a745';
    if (status.canWorkWithoutDocuments) return '#fd7e14';
    return '#dc3545';
  };

  const hasAnyAssignments = () => {
    return normalizeStations(dayPlan).stations.some(station => 
      Object.values(station.hourlyStaffing).some(staffing => 
        staffing.assigned && staffing.assigned.length > 0
      )
    );
  };

  const getUserStatusEmoji = (userId) => {
    const status = userStatuses[userId];
    if (!status) return '❓';
    
    if (status.allComplete) return '✅';
    if (status.canWorkWithoutDocuments) return '⚠️';
    return '❌';
  };

  const updateTimeRange = (field, value) => {
    const updatedPlan = {
      ...dayPlan,
      [field]: value
    };
    
    // Berechne neue aktive Stunden basierend auf geänderten Zeiten
    const startTime = field === 'startTime' ? value : (dayPlan.startTime || '07:00');
    const endTime = field === 'endTime' ? value : (dayPlan.endTime || '01:00');
    
    const startHour = parseInt(startTime.split(':')[0]);
    let endHour = parseInt(endTime.split(':')[0]);
    if (endHour < startHour) {
      endHour = endHour + 24; // Nächster Tag
    }
    
    // Aktualisiere alle Stationen: Stunden außerhalb des Bereichs auf needed: 0 setzen
    updatedPlan.stations = updatedPlan.stations.map(station => ({
      ...station,
      hourlyStaffing: Object.keys(station.hourlyStaffing).reduce((acc, hourKey) => {
        const hour = parseInt(hourKey);
        const isActiveHour = hour >= startHour && hour <= endHour;
        
        acc[hourKey] = {
          ...station.hourlyStaffing[hourKey],
          // Setze auf 0 wenn außerhalb der aktiven Zeit, behalte aktuellen Wert wenn innerhalb
          needed: isActiveHour ? station.hourlyStaffing[hourKey].needed : 0
        };
        return acc;
      }, {})
    }));
    
    setDayPlan(updatedPlan);
  };

  const getActiveHours = () => {
    // Fallback-Werte für den Fall, dass startTime oder endTime null/undefined sind
    const startTime = dayPlan.startTime || '07:00';
    const endTime = dayPlan.endTime || '01:00';
    
    const start = parseInt(startTime.split(':')[0]);
    let end = parseInt(endTime.split(':')[0]);
    
    // Wenn End-Zeit am nächsten Tag ist (z.B. 01:00), dann zu 25 konvertieren
    if (end < start) {
      end = end + 24;
    }

    // An Frühöffnungs-Tagen (Sa/Feiertag) beginnt das Raster bei 05:00
    const effectiveStart = isEarlyOpeningDay(selectedDate) ? Math.min(start, 5) : start;
    return hours.filter(hour => hour >= effectiveStart && hour <= end);
  };

  const copyStaffingFromPreviousHour = (stationIndex, hour) => {
    const newDayPlan = { ...dayPlan };
    const station = newDayPlan.stations[stationIndex];
    
    if (hour > 6) {
      const prevHour = hour - 1;
      if (station.hourlyStaffing[prevHour]) {
        station.hourlyStaffing[hour].needed = station.hourlyStaffing[prevHour].needed;
      }
    }
    
    setDayPlan(newDayPlan);
  };

  const fillStaffingPattern = (stationIndex, pattern) => {
    const newDayPlan = { ...dayPlan };
    const station = newDayPlan.stations[stationIndex];
    const activeHours = getActiveHours();
    
    switch (pattern) {
      case 'morning-peak':
        // Morgens: 1 → 2 → 3 → 2 → 1
        activeHours.forEach((hour, index) => {
          if (index < 2) station.hourlyStaffing[hour].needed = 1;
          else if (index < 4) station.hourlyStaffing[hour].needed = 2;
          else if (index < 6) station.hourlyStaffing[hour].needed = 3;
          else if (index < 8) station.hourlyStaffing[hour].needed = 2;
          else station.hourlyStaffing[hour].needed = 1;
        });
        break;
      case 'lunch-peak':
        // Mittags-Peak: 1 → 3 → 5 → 3 → 1
        activeHours.forEach((hour, index) => {
          if (index < 2) station.hourlyStaffing[hour].needed = 1;
          else if (index < 4) station.hourlyStaffing[hour].needed = 3;
          else if (index < 6) station.hourlyStaffing[hour].needed = 5;
          else if (index < 8) station.hourlyStaffing[hour].needed = 3;
          else station.hourlyStaffing[hour].needed = 1;
        });
        break;
      case 'steady':
        // Konstant 2 Personen
        activeHours.forEach(hour => {
          station.hourlyStaffing[hour].needed = 2;
        });
        break;
      default:
        break;
    }
    
    setDayPlan(newDayPlan);
  };

  const resetDayPlan = async () => {
    if (await confirmDialog('Möchten Sie wirklich alle Einträge für diesen Tag löschen?')) {
      const resetPlan = {
        date: selectedDate,
        startTime: '07:00',
        endTime: '01:00',
        stations: stations.map(station => ({
          station_id: station.id,
          hourlyStaffing: hours.reduce((acc, hour) => {
            acc[hour] = { 
              needed: 0, 
              assigned: [],
              needsCashier: hour < 22 // Standard: Kassenkraft bis 22 Uhr benötigt
            };
            return acc;
          }, {})
        }))
      };
      setDayPlan(resetPlan);
      setCurrentPlanId(null); // Plan-ID zurücksetzen
      showToast('Tag wurde zurückgesetzt!', 'info');
    }
  };

  const generateDateRangeDays = () => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    const days = [];
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const isSelected = dateStr === selectedDate;
      const hasData = plannedDays.includes(dateStr);
      const dayName = d.toLocaleDateString('de-DE', { weekday: 'short' });
      
      days.push({
        date: dateStr,
        day: d.getDate(),
        dayName,
        isToday,
        isSelected,
        hasData
      });
    }
    
    return days;
  };

  const updateDateRange = (field, value) => {
    const newRange = { ...dateRange, [field]: value };
    setDateRange(newRange);
    
    // Speichere die neue Auswahl im localStorage
    try {
      localStorage.setItem('planningOverview_dateRange', JSON.stringify(newRange));
    } catch (error) {
      console.log('Fehler beim Speichern der Datumsauswahl:', error);
    }
    
    // Nach einer kurzen Verzögerung die geplanten Tage neu laden
    setTimeout(() => {
      fetchPlannedDays();
    }, 300);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0"><i className="bi bi-clock-fill me-2 text-success"></i>Bedarfsplanung</h2>
          <small className="text-muted">Hier planst du, <strong>wie viele Mitarbeiter</strong> du pro Stunde und Station brauchst. Die konkrete Zuteilung von Personen erfolgt unter „Schichtzuteilung".</small>
        </div>
        <div className="d-flex gap-2">
          <span className="badge bg-info">Automatische Speicherung aktiviert</span>
          <button 
            className="btn btn-secondary"
            onClick={() => {
              setCloneSourceDate(selectedDate); // Setze aktuell markierten Tag als Quell-Datum
              setShowCloneModal(true);
            }}
          >
            <i className="bi bi-copy me-2"></i>Tag klonen
          </button>
          <button 
            className="btn btn-danger"
            onClick={resetDayPlan}
          >
            <i className="bi bi-trash me-2"></i>Tag zurücksetzen
          </button>
          <button 
            className="btn btn-success"
            onClick={saveDayPlan}
          >
            <i className="bi bi-save me-2"></i>Personalbedarf speichern
          </button>
        </div>
      </div>

      <div className="alert alert-info">
        <h5><i className="bi bi-lightbulb me-2"></i>Personalbedarf planen</h5>
        <p className="mb-2">📋 <strong>Personalbedarf eingeben:</strong> Anzahl Personen pro Station/Stunde festlegen</p>
        <p className="mb-2">💰 <strong>Kassenkraft markieren:</strong> Checkbox ankreuzen falls Kassenberechtigung erforderlich</p>
        <p className="mb-2">💾 <strong>Bedarf speichern:</strong> "Personalbedarf speichern" klicken</p>
        <p className="mb-0">� <strong>Tag klonen:</strong> Personalbedarf auf andere Tage übertragen</p>
        <p className="mb-0 mt-2"><em>Die Personalzuweisung erfolgt später in einem separaten Menüpunkt.</em></p>
      </div>

      {/* Datum und Grundeinstellungen */}
      <div className="card mb-4">
        <div className="card-header">
          <h5><i className="bi bi-calendar-date me-2"></i>Tagesplanung</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <label className="form-label">Datum</label>
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Filter</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="cashierFilter"
                  checked={cashierFilter}
                  onChange={(e) => setCashierFilter(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="cashierFilter">
                  💰 Nur Kassenkräfte anzeigen
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Planungsübersicht 
                <small className="text-muted ms-2">(Auswahl wird gespeichert)</small>
              </label>
              <div className="border rounded p-3">
                {/* Von-Bis Datumseingabe */}
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small">Von</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={dateRange.start}
                      onChange={(e) => updateDateRange('start', e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Bis</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={dateRange.end}
                      onChange={(e) => updateDateRange('end', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Tage-Übersicht */}
                <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {generateDateRangeDays().map((day) => (
                    <button
                      key={day.date}
                      className={`btn btn-sm ${
                        day.isSelected ? 'btn-primary' : 
                        day.hasData ? 'btn-success' : 
                        day.isToday ? 'btn-outline-primary' : 
                        'btn-outline-secondary'
                      }`}
                      style={{ 
                        fontSize: '11px', 
                        padding: '4px 8px',
                        minWidth: '45px'
                      }}
                      onClick={() => setSelectedDate(day.date)}
                      title={
                        day.hasData ? `${day.date} - Schichtplan vorhanden` : 
                        day.isToday ? `${day.date} - Heute` : 
                        day.date
                      }
                    >
                      <div style={{ lineHeight: '1.1' }}>
                        <div className="fw-bold">{day.day}</div>
                        <div style={{ fontSize: '9px', opacity: '0.8' }}>{day.dayName}</div>
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="mt-2">
                  <small className="text-muted">
                    <span className="badge bg-success me-1">●</span>Geplant
                    <span className="badge bg-primary mx-1">●</span>Ausgewählt
                    <span className="badge bg-outline-primary ms-1">●</span>Heute
                  </small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="row">
                <div className="col-6">
                  <label className="form-label">Start-Zeit</label>
                  <input
                    type="time"
                    className="form-control"
                    value={dayPlan.startTime}
                    onChange={(e) => updateTimeRange('startTime', e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">End-Zeit</label>
                  <input
                    type="time"
                    className="form-control"
                    value={dayPlan.endTime}
                    onChange={(e) => updateTimeRange('endTime', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stundenbasierte Planung - Horizontale Tabelle */}
      <div className="card">
        <div className="card-header">
          <h5><i className="bi bi-calendar2-week me-2"></i>Personalbedarf pro Stunde</h5>
        </div>
        <div className="card-body">
          {/* Grafische Übersicht */}
          <div className="mb-4">
            <h6 className="mb-3">Grafische Übersicht</h6>
            {normalizeStations(dayPlan).stations.map((station, stationIndex) => (
              <div key={station.station_id} className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <div 
                    className="rounded me-2" 
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      backgroundColor: stations.find(s => s.id === station.station_id)?.color 
                    }}
                  ></div>
                  <strong>{getStationName(station.station_id)}</strong>
                </div>
                <div className="d-flex" style={{ height: '60px' }}>
                  {getActiveHours().map(hour => {
                    const needed = station.hourlyStaffing[hour]?.needed || 0;
                    const maxHeight = Math.max(...normalizeStations(dayPlan).stations.flatMap(s => 
                      getActiveHours().map(h => s.hourlyStaffing[h]?.needed || 0)
                    ));
                    const height = maxHeight > 0 ? (needed / maxHeight) * 100 : 0;
                    
                    return (
                      <div 
                        key={hour}
                        className="d-flex flex-column justify-content-end me-1"
                        style={{ width: '30px', height: '60px' }}
                      >
                        <div 
                          className="rounded-top"
                          style={{ 
                            backgroundColor: stations.find(s => s.id === station.station_id)?.color + '80',
                            height: `${height}%`,
                            minHeight: needed > 0 ? '5px' : '0px',
                            transition: 'height 0.3s ease'
                          }}
                          title={`${formatHour(hour)}: ${needed} Personen`}
                        ></div>
                        <small className="text-center mt-1" style={{ fontSize: '10px' }}>
                          {needed > 0 ? needed : ''}
                        </small>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Eingabetabelle */}
          <div className="table-responsive">
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>Station</th>
                  {getActiveHours().map(hour => (
                    <th key={hour} className="text-center" style={{ minWidth: '80px' }}>
                      <div className="fw-bold">{formatHour(hour)}</div>
                      <small className="text-muted d-block">{formatHour(hour + 1)}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {normalizeStations(dayPlan).stations.map((station, stationIndex) => (
                  <tr key={station.station_id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div 
                          className="rounded me-2" 
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: stations.find(s => s.id === station.station_id)?.color 
                          }}
                        ></div>
                        <strong>{getStationName(station.station_id)}</strong>
                      </div>
                    </td>
                    {getActiveHours().map(hour => (
                      <td key={hour} className="text-center p-2">
                        <div className="d-flex flex-column align-items-center gap-1">
                          {/* Personalbedarf Eingabe */}
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            style={{ width: '50px' }}
                            min="0"
                            max="10"
                            value={station.hourlyStaffing[hour]?.needed || 0}
                            onChange={(e) => updateStaffingNeed(stationIndex, hour, e.target.value)}
                            placeholder="0"
                            disabled={!isHourEditable(station.station_id, hour)}
                            title={!isHourEditable(station.station_id, hour) ? 'Vor 07:00 nur Wiesn-Garten (Sa/Feiertag)' : undefined}
                          />
                          {/* Kassenkraft Checkbox */}
                          <div className="form-check form-check-sm">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`cashier-${station.station_id}-${hour}`}
                              checked={station.hourlyStaffing[hour]?.needsCashier || false}
                              onChange={(e) => updateCashierNeed(stationIndex, hour, e.target.checked)}
                              disabled={!isHourEditable(station.station_id, hour)}
                            />
                            <label className="form-check-label" htmlFor={`cashier-${station.station_id}-${hour}`} title="Kassenkraft benötigt">
                              💰
                            </label>
                          </div>
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Tagesübersicht */}
      <div className="card mt-4">
        <div className="card-header">
          <h5><i className="bi bi-graph-up me-2"></i>Tagesübersicht</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Zeit</th>
                  {normalizeStations(dayPlan).stations.map(station => (
                    <th key={station.station_id} style={{ color: stations.find(s => s.id === station.station_id)?.color }}>
                      {getStationName(station.station_id)}
                    </th>
                  ))}
                  <th>Gesamt</th>
                  <th>Verfügbar</th>
                </tr>
              </thead>
              <tbody>
                {getActiveHours().map(hour => (
                  <tr key={hour}>
                    <td className="fw-bold">{formatHour(hour)}</td>
                    {normalizeStations(dayPlan).stations.map(station => (
                      <td key={station.station_id}>
                        <span className={`badge ${
                          station.hourlyStaffing[hour]?.assigned?.length >= station.hourlyStaffing[hour]?.needed 
                            ? 'bg-success' 
                            : station.hourlyStaffing[hour]?.needed > 0 
                              ? 'bg-warning' 
                              : 'bg-secondary'
                        }`}>
                          {station.hourlyStaffing[hour]?.assigned?.length || 0}/{station.hourlyStaffing[hour]?.needed || 0}
                        </span>
                      </td>
                    ))}
                    <td>
                      <strong>
                        {normalizeStations(dayPlan).stations.reduce((sum, station) => 
                          sum + (station.hourlyStaffing[hour]?.needed || 0), 0
                        )} Personen
                      </strong>
                    </td>
                    <td>
                      <span className={`badge ${
                        getAvailableEmployeesCount(hour) > 0 ? 'bg-info' : 'bg-danger'
                      }`}>
                        {getAvailableEmployeesCount(hour)} verfügbar
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast.show && (
        <div 
          className={`position-fixed top-0 end-0 m-3 alert alert-${toast.type} alert-dismissible fade show`}
          style={{ zIndex: 1055 }}
          role="alert"
        >
          <i className={`bi ${toast.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
          {toast.message}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setToast({ show: false, message: '', type: 'success' })}
          ></button>
        </div>
      )}

      {/* Klon-Modal */}
      {showCloneModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-copy me-2"></i>Tag klonen
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCloneModal(false);
                    setCloneSourceDate(''); // Reset zum leeren Wert
                    setCloneTargetDate('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Kopiere die Personalbedarf-Einstellungen und Kassenkraft-Anforderungen von einem Tag auf einen anderen.</p>
                
                <div className="mb-3">
                  <label htmlFor="cloneSourceDate" className="form-label">Von welchem Tag kopieren?</label>
                  <input
                    type="date"
                    className="form-control"
                    id="cloneSourceDate"
                    value={cloneSourceDate}
                    onChange={(e) => setCloneSourceDate(e.target.value)}
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="cloneTargetDate" className="form-label">Auf welchen Tag kopieren?</label>
                  <input
                    type="date"
                    className="form-control"
                    id="cloneTargetDate"
                    value={cloneTargetDate}
                    onChange={(e) => setCloneTargetDate(e.target.value)}
                  />
                </div>
                
                <div className="alert alert-info">
                  <small>
                    <i className="bi bi-info-circle me-1"></i>
                    Es werden nur die Personalbedarf-Zahlen und Kassenkraft-Einstellungen kopiert. 
                    Die zugewiesenen Mitarbeiter werden NICHT mitkopiert.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCloneModal(false);
                    setCloneSourceDate('');
                    setCloneTargetDate('');
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!cloneSourceDate || !cloneTargetDate}
                  onClick={() => {
                    cloneDayPlan(cloneSourceDate, cloneTargetDate);
                    setShowCloneModal(false);
                    setCloneSourceDate('');
                    setCloneTargetDate('');
                  }}
                >
                  <i className="bi bi-copy me-2"></i>Klonen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlexibleShiftPlanning;
